// DispatchDeck — Check Printer Utility
// Generates check PDFs for standard 3-per-page check stock (8.5" x 11")
// Uses jsPDF loaded dynamically from CDN
console.log('[CheckPrinter] Module loaded');

let jsPDFLoaded = false;
let jsPDFClass = null;

// ── Load jsPDF dynamically ─────────────────────────────────────────
async function loadJsPDF() {
  if (jsPDFLoaded && jsPDFClass) return jsPDFClass;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/vendor/jspdf.umd.min.js';
    script.onload = () => {
      jsPDFClass = window.jspdf?.jsPDF;
      if (jsPDFClass) {
        jsPDFLoaded = true;
        resolve(jsPDFClass);
      } else {
        reject(new Error('jsPDF not found after loading'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load jsPDF'));
    document.head.appendChild(script);
  });
}

// ── Number to words (for written amount line) ──────────────────────
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function numberToWords(num) {
  if (num === 0) return 'Zero';
  if (num < 0) return 'Negative ' + numberToWords(-num);

  let words = '';

  if (Math.floor(num / 1000000) > 0) {
    words += numberToWords(Math.floor(num / 1000000)) + ' Million ';
    num %= 1000000;
  }
  if (Math.floor(num / 1000) > 0) {
    words += numberToWords(Math.floor(num / 1000)) + ' Thousand ';
    num %= 1000;
  }
  if (Math.floor(num / 100) > 0) {
    words += ONES[Math.floor(num / 100)] + ' Hundred ';
    num %= 100;
  }
  if (num > 0) {
    if (num < 20) {
      words += ONES[num];
    } else {
      words += TENS[Math.floor(num / 10)];
      if (num % 10 > 0) words += '-' + ONES[num % 10];
    }
  }

  return words.trim();
}

function amountToWords(amount) {
  const dollars = Math.floor(amount);
  const cents = Math.round((amount - dollars) * 100);
  const dollarWords = numberToWords(dollars);
  return `${dollarWords} and ${String(cents).padStart(2, '0')}/100`;
}

// ── MICR line formatting ───────────────────────────────────────────
// Uses unicode symbols that approximate MICR E-13B characters
// For production printing, users need MICR toner + proper E-13B font installed
function formatMICR(routing, account, checkNo) {
  // Standard MICR format: ⑆routing⑆ account⑈ checkno
  // Using C (transit), A (on-us), D (dash) MICR symbols
  const r = String(routing).padStart(9, '0');
  const a = String(account);
  const c = String(checkNo).padStart(6, '0');
  return `C${r}C ${a}A ${c}`;
}

// ── Check layout constants (in points, 72pt = 1 inch) ──────────────
const PAGE_W = 612; // 8.5"
const PAGE_H = 792; // 11"
const CHECK_H = 252; // 3.5" for check area
const STUB_H = 270; // 3.75" for each stub
const MARGIN = 36; // 0.5" margins
const INNER_W = PAGE_W - MARGIN * 2;

// ── Generate check PDF ─────────────────────────────────────────────
/**
 * Generate a check PDF for one or more invoices paid to the same vendor.
 * @param {Object} opts
 * @param {Object} opts.bankAccount - { companyName, address1, address2, address3, bankName, routingNumber, accountNumber }
 * @param {number} opts.checkNumber - Check number to print
 * @param {string} opts.payee - Vendor name
 * @param {number} opts.amount - Total amount
 * @param {string} opts.date - Check date (formatted string)
 * @param {string} opts.memo - Memo line text
 * @param {Array} opts.invoices - Array of { invoiceNumber, amount, description } for stub detail
 * @returns {Promise<Blob>} PDF blob
 */
export async function generateCheckPDF(opts) {
  const JsPDF = await loadJsPDF();
  const pdf = new JsPDF({ unit: 'pt', format: 'letter' });

  const { bankAccount, checkNumber, payee, amount, date, memo, invoices } = opts;
  const ba = bankAccount;

  // ── CHECK AREA (top 3.5") ──────────────────────────────────────
  const checkTop = 0;

  // Company info (top-left)
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(ba.companyName || '', MARGIN, checkTop + 36);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  let addrY = checkTop + 48;
  if (ba.address1) { pdf.text(ba.address1, MARGIN, addrY); addrY += 10; }
  if (ba.address2) { pdf.text(ba.address2, MARGIN, addrY); addrY += 10; }
  if (ba.address3) { pdf.text(ba.address3, MARGIN, addrY); addrY += 10; }

  // Check number + date (top-right)
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Check #${checkNumber}`, PAGE_W - MARGIN, checkTop + 36, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Date: ${date}`, PAGE_W - MARGIN, checkTop + 50, { align: 'right' });

  // Pay to the order of
  const payLineY = checkTop + 90;
  pdf.setFontSize(8);
  pdf.text('PAY TO THE', MARGIN, payLineY - 4);
  pdf.text('ORDER OF', MARGIN, payLineY + 6);

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text(payee || '', MARGIN + 60, payLineY + 4);

  // Amount box (right side)
  const amtBoxX = PAGE_W - MARGIN - 120;
  pdf.setDrawColor(0);
  pdf.setLineWidth(1);
  pdf.rect(amtBoxX, payLineY - 12, 120, 22);
  pdf.setFontSize(12);
  pdf.text(`$${amount.toFixed(2)}`, amtBoxX + 8, payLineY + 4);

  // Draw line under payee
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN + 60, payLineY + 8, amtBoxX - 10, payLineY + 8);

  // Written amount line
  const wordsY = payLineY + 30;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const amountWords = amountToWords(amount);
  const wordsPadded = amountWords + ' ' + '-'.repeat(Math.max(0, 80 - amountWords.length));
  pdf.text(wordsPadded, MARGIN, wordsY);
  pdf.setFontSize(7);
  pdf.text('DOLLARS', PAGE_W - MARGIN, wordsY, { align: 'right' });
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, wordsY + 4, PAGE_W - MARGIN, wordsY + 4);

  // Bank name
  pdf.setFontSize(8);
  pdf.text(ba.bankName || '', MARGIN, wordsY + 24);

  // Memo line
  const memoY = wordsY + 44;
  pdf.setFontSize(8);
  pdf.text('Memo:', MARGIN, memoY);
  pdf.text(memo || '', MARGIN + 30, memoY);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN + 30, memoY + 4, PAGE_W / 2 - 20, memoY + 4);

  // Signature line
  pdf.line(PAGE_W / 2 + 40, memoY + 4, PAGE_W - MARGIN, memoY + 4);
  pdf.setFontSize(7);
  pdf.text('Authorized Signature', PAGE_W - MARGIN, memoY + 14, { align: 'right' });

  // MICR line (bottom of check area)
  const micrY = checkTop + CHECK_H - 20;
  pdf.setFont('courier', 'bold');
  pdf.setFontSize(12);
  const micrText = formatMICR(ba.routingNumber, ba.accountNumber, checkNumber);
  pdf.text(micrText, MARGIN + 40, micrY);

  // ── Separator line ─────────────────────────────────────────────
  pdf.setDrawColor(180);
  pdf.setLineWidth(0.5);
  pdf.setLineDashPattern([4, 2], 0);
  pdf.line(MARGIN, CHECK_H, PAGE_W - MARGIN, CHECK_H);
  pdf.setLineDashPattern([], 0);

  // ── STUB 1 (Vendor copy) ──────────────────────────────────────
  drawStub(pdf, CHECK_H + 4, 'Vendor Copy', { checkNumber, date, payee, amount, memo, invoices });

  // ── Separator line ─────────────────────────────────────────────
  pdf.setDrawColor(180);
  pdf.setLineWidth(0.5);
  pdf.setLineDashPattern([4, 2], 0);
  pdf.line(MARGIN, CHECK_H + STUB_H, PAGE_W - MARGIN, CHECK_H + STUB_H);
  pdf.setLineDashPattern([], 0);

  // ── STUB 2 (Company copy) ─────────────────────────────────────
  drawStub(pdf, CHECK_H + STUB_H + 4, 'Company Records', { checkNumber, date, payee, amount, memo, invoices });

  return pdf.output('blob');
}

function drawStub(pdf, top, label, data) {
  const { checkNumber, date, payee, amount, memo, invoices } = data;

  // Header
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  pdf.setTextColor(120);
  pdf.text(label, PAGE_W - MARGIN, top + 12, { align: 'right' });
  pdf.setTextColor(0);

  // Check info
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Check #${checkNumber}`, MARGIN, top + 20);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Date: ${date}`, MARGIN + 120, top + 20);
  pdf.text(`Amount: $${amount.toFixed(2)}`, MARGIN + 260, top + 20);

  pdf.setFontSize(9);
  pdf.text(`Payee: ${payee || ''}`, MARGIN, top + 36);
  if (memo) pdf.text(`Memo: ${memo}`, MARGIN, top + 48);

  // Invoice detail table
  if (invoices?.length) {
    let y = top + 64;
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Invoice #', MARGIN, y);
    pdf.text('Description', MARGIN + 100, y);
    pdf.text('Amount', PAGE_W - MARGIN, y, { align: 'right' });
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y + 4, PAGE_W - MARGIN, y + 4);
    y += 14;

    pdf.setFont('helvetica', 'normal');
    for (const inv of invoices.slice(0, 12)) { // max 12 lines on stub
      pdf.text(inv.invoiceNumber || '', MARGIN, y);
      pdf.text((inv.description || '').substring(0, 50), MARGIN + 100, y);
      pdf.text(`$${(inv.amount || 0).toFixed(2)}`, PAGE_W - MARGIN, y, { align: 'right' });
      y += 11;
    }
    if (invoices.length > 12) {
      pdf.text(`... and ${invoices.length - 12} more`, MARGIN, y);
    }
  }
}

// ── Preview + Print flow ───────────────────────────────────────────
export async function previewAndPrintCheck(opts) {
  try {
    window.showToast?.('Generating check preview...', 'info');
    const blob = await generateCheckPDF(opts);
    const url = URL.createObjectURL(blob);

    // Open in new tab for print
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => printWindow.print(), 500);
      };
    } else {
      // Popup blocked — fall back to download
      const a = document.createElement('a');
      a.href = url;
      a.download = `check-${opts.checkNumber}.pdf`;
      a.click();
      window.showToast?.('Check PDF downloaded (popup blocked — enable popups for print preview)', 'info');
    }

    // Clean up URL after a delay
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (err) {
    console.error('[CheckPrinter] Error:', err);
    window.showToast?.('Failed to generate check: ' + err.message, 'error');
    return false;
  }
}

export default { generateCheckPDF, previewAndPrintCheck, amountToWords, numberToWords };
